/* NIVARI newsletter pop-up — a thin PopIn subclass, not a theme.js edit.
   theme.js is a protected Prestige file (CLAUDE.md); this reaches its
   exported `PopIn` class via a dynamic import() of the "theme" bare
   specifier already declared in theme.liquid's importmap. That works
   from a plain (non type="module") <script> the same way
   nivari-configurator.js's existing `await import("vendor")` does —
   dynamic import() honours the page importmap regardless of the calling
   script's own type, so this file doesn't need to become a module itself.

   PopIn (see theme.js) already gives this element everything else this
   task asked for, for free: role="dialog" (DialogElement), the
   show()/hide() lifecycle, a light-DOM (non-shadow-root) render target
   matching this section's existing markup, click-outside-to-close
   (clickOutsideDeactivates defaults to true), and — matching this task's
   own "slight rise from bottom + subtle opacity, no bounce" motion spec
   exactly — PopIn's own createEnterAnimationControls/createLeaveAnimationControls
   (translateY(25px)->0 with an opacity fade). None of that is reimplemented
   here.

   The one real gap for a prominent, large "modal" (vs. PopIn's default
   small corner-popup use, e.g. the cookie privacy-banner): PopIn
   overrides shouldTrapFocus to false. The three overrides below turn
   that back on for this specific element only — every other PopIn
   instance in the theme (privacy-banner, etc.) is unaffected — which
   also switches on the focus-trap library's own default Escape-to-close
   and return-focus-on-close behaviour for free (see DialogElement's
   focusTrap getter and attributeChangedCallback in theme.js — neither
   is reimplemented here either). */
(async () => {
  if (window.customElements.get('nivari-newsletter-popup')) {
    return;
  }

  const { PopIn } = await import('theme');
  const { animate } = await import('vendor');

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class NivariNewsletterPopup extends PopIn {
    connectedCallback() {
      super.connectedCallback();
      this.setAttribute('aria-modal', 'true');
    }

    get shouldTrapFocus() {
      return true;
    }

    get shouldLock() {
      return true;
    }

    /* PopIn's own rise+fade (see theme.js) isn't reduced-motion-aware — that
       is an existing characteristic of every PopIn/dialog in this theme
       (confirmed against theme.js/vendor.min.js: none of the shared dialog
       animation plumbing checks this media feature). Rather than change
       that shared lifecycle, override just these two hooks — already
       designed to be overridable per-subclass — so this specific popup
       drops to a brief opacity-only fade (no translateY motion, no
       bounce/spring) when the visitor has asked for reduced motion. */
    createEnterAnimationControls() {
      if (prefersReducedMotion()) {
        return animate(this, { opacity: [0, 1] }, { duration: 0.15 });
      }

      return super.createEnterAnimationControls();
    }

    createLeaveAnimationControls() {
      if (prefersReducedMotion()) {
        return animate(this, { opacity: [1, 0] }, { duration: 0.15 });
      }

      return super.createLeaveAnimationControls();
    }
  }

  window.customElements.define('nivari-newsletter-popup', NivariNewsletterPopup);
})();

/* Scrim click-to-close. Verified directly against theme.js/vendor.min.js:
   DialogElement never passes focus-trap's own `clickOutsideDeactivates`
   option to the trap (only `allowOutsideClick`), and per the bundled
   focus-trap source, that option alone never calls the trap's own
   deactivate() on an outside click — confirmed live that outside clicks
   do not close this (or any other) dialog in this theme today. That is
   an existing characteristic of DialogElement, not something introduced
   by the scrim markup, so rather than fight or reimplement that core
   lifecycle this adds one small, scoped, delegated listener for this
   popup's own scrim only. */
document.addEventListener('click', (event) => {
  const scrim = event.target.closest('.nivari-newsletter-popup-scrim');

  if (!scrim) {
    return;
  }

  const popup = document.querySelector('nivari-newsletter-popup[open]');

  if (popup) {
    popup.hide();
  }
});
